import { AlertTriangle, Archive, Barcode, CheckCircle2, CircleHelp, Coins, ImageOff, ImagePlus, Link2, PencilLine, Plus, RefreshCw, Save, Search, Trash2, Undo2 } from 'lucide-react'
import { useEffect, useId, useState, type ReactNode } from 'react'

import { ActionInfo, ActionInfoQuiet, Button, ModalFrame, ScreenInfo } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import { ScanPhotoRun } from '../../scan-session/ui/ScanPhotoRun'
import { CASH_FIELD_TITLES, UNKNOWN_LABEL, optionsFor, shownValue, undefinedCashFields } from '../model/cashFields'
import {
  FIELD_LABELS,
  barcodeAdded,
  barcodeRemoved,
  blockerOf,
  discarded,
  fieldChanged,
  initialState,
  isDirty,
  mineKept,
  serverTaken,
  withServer,
} from '../model/draft'
import type { CommonProductActions, CommonProductVm, EditorState, ProductFieldKey, ProductSourceVm } from '../model/types'
import './commonProduct.css'

/** Справка окна — единственная ⓘ, сверху слева: коротко и обычными словами (решение владельца 23.10.2026). */
export const COMMON_PRODUCT_INFO = {
  title: 'Общий товар',
  lines: [
    'Общий товар — главная карточка: здесь он создаётся, правится и уходит в архив.',
    'Нажмите «Сохранить» — изменение запишется в общий каталог, а базы и кабинеты подтянут его по настройкам синхронизации.',
    'Внизу «Сохранить» и «Отменить изменения» (спросит подтверждение). Закрыть окно с несохранённой правкой можно только после подтверждения.',
    'Кассовые поля (ставка НДС, единица, весовой признак, предмет и способ расчёта) можно не знать: пустое поле — «Не знаю», а не «Без НДС» или «Товар». Такой товар заводится, но своя касса его не продаёт, пока поле не определено. Если ставку и единицу называют источники, они подставятся сами: сначала AQSI, затем 1С.',
    'Новое фото сначала показывается без фона с пунктирной рамкой — там пройдёт обрезка. Режет лишнее — потяните рамку за угол или сторону (для точности увеличьте фото) и нажмите «Принять»; «Отмена» — не загружать.',
  ],
} as const

const pad = (value: number): string => String(value).padStart(2, '0')

const timeOf = (iso: string): string => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? ''
    : `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Окно «Общий товар» (RP-FB50): просмотр и правка полей общего товара, штрихкоды списком, связи с источниками, архив.
 * Вёрстка от телефона (360 px): один столбец, цели нажатия не меньше 44 px, поля одной высоты, действия — в липкой панели
 * внизу. Сверху — крестик рамки окна; внизу «Закрыть» только когда правки нет, иначе «Сохранить» и «Отменить изменения»
 * (с подтверждением). Закрытие окна с несохранённой правкой спрашивает (сторож закрытия).
 *
 * Если товар за время правки изменил кто-то ещё, экран показывает «было / сейчас / ваше» и ждёт решения человека.
 * Ошибки — дословно, с кодом и номером запроса для копирования, и не пропадают сами.
 */
export function CommonProductScreen({
  vm,
  actions,
  renderSourceExtension,
  photos,
}: {
  vm: CommonProductVm
  actions: CommonProductActions
  /** Расширение карточки одного источника (сводка и кнопка открытия карточки источника): строит binding по реестру описаний видов. */
  renderSourceExtension?: ((source: ProductSourceVm) => ReactNode) | undefined
  /** Галерея фото общего товара; нет — секции фото нет. */
  photos?: ReactNode
}) {
  const [state, setState] = useState<EditorState>(() => startState(vm))
  const [newBarcode, setNewBarcode] = useState('')
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)

  // Свежая версия товара — после чтения, своего сохранения или чужой правки: поля сверяются, а не затираются.
  const product = vm.product
  const [seenProduct, setSeenProduct] = useState(product)
  if (product !== seenProduct) {
    setSeenProduct(product)
    if (product !== null) setState((current) => withServer(current, product))
  }

  const dirty = isDirty(state)
  const create = vm.mode === 'create'
  const archived = product?.archived === true
  const loaded = create || product !== null
  const duplicate = vm.duplicate ?? null
  const photoRun = vm.photoRun ?? null
  // Проверка дубля и загрузка кадра после создания держат форму: второе нажатие не нужно и опасно (второй товар).
  const locked = vm.save.busy || vm.archive.busy || duplicate?.kind === 'checking' || photoRun !== null
  const editable = loaded && !archived
  const blocker = blockerOf(state.mine)
  const missingCash = undefinedCashFields(state.mine)
  const conflicting = state.conflicts.length > 0
  const canSave = editable && dirty && !locked && !conflicting && blocker === null

  // Сторож закрытия окна: пока есть несохранённая правка, крестик рамки и Esc сперва спрашивают.
  const { closeGuard } = actions
  useEffect(() => {
    if (!dirty) {
      closeGuard(null)
      return undefined
    }
    closeGuard(() => {
      setConfirmClose(true)
      return false
    })
    return () => closeGuard(null)
  }, [closeGuard, dirty])

  const save = () => {
    if (!canSave) return
    actions.save(
      create
        ? { mode: 'create', form: state.mine }
        : { mode: 'edit', expectedVersion: state.version, base: state.base, form: state.mine },
    )
  }
  const addBarcode = () => {
    if (newBarcode.trim() === '') return
    setState((current) => barcodeAdded(current, newBarcode))
    setNewBarcode('')
  }
  const closeNow = () => {
    closeGuard(null)
    setConfirmClose(false)
    actions.close()
  }
  const title = create ? 'Новый общий товар' : (product?.name ?? 'Общий товар')
  const barcodesNote = state.conflicts.some((conflict) => conflict.field === 'barcodes') ? null : noteOf(state, 'barcodes')

  return (
    <ActionInfoQuiet>
      <section className="platform-product" aria-label={title}>
        <header className="platform-product__head">
          <ScreenInfo title={COMMON_PRODUCT_INFO.title} lines={COMMON_PRODUCT_INFO.lines} />
          <h2>{title}</h2>
          {!create && (
            <Button
              variant="ghost"
              icon={<RefreshCw size={16} aria-hidden="true" />}
              busy={vm.query.refreshing}
              onClick={actions.reload}
              actionId="platform.common-product.reload"
            >
              Обновить
            </Button>
          )}
        </header>

        {!create && vm.query.status !== 'ready' && <QueryState query={vm.query} emptyText="Товар не найден" onRetry={actions.reload} />}

        {archived && (
          <div className="platform-product__banner" role="status">
            <Archive size={18} aria-hidden="true" />
            <p>Товар в архиве: правка закрыта. Связи с источниками остаются, архивный товар в источники больше не раздаётся.</p>
          </div>
        )}

        {dirty && !archived && (
          <div className="platform-product__banner" role="status">
            <PencilLine size={18} aria-hidden="true" />
            <p>Есть несохранённые изменения.</p>
          </div>
        )}

        {conflicting && (
          <section className="platform-product__section" role="alert" aria-label="Товар изменили, пока вы правили">
            <h3>
              <AlertTriangle size={16} aria-hidden="true" /> Кто-то изменил товар, пока вы правили
            </h3>
            <ul className="platform-product__conflicts">
              {state.conflicts.map((conflict) => (
                <li key={conflict.field} className="platform-product__conflict">
                  <p className="platform-product__conflict-field">{FIELD_LABELS[conflict.field]}</p>
                  <dl>
                    <div>
                      <dt>Было</dt>
                      <dd>{shownValue(conflict.field, conflict.was) || '—'}</dd>
                    </div>
                    <div>
                      <dt>Сейчас</dt>
                      <dd>{shownValue(conflict.field, conflict.server) || '—'}</dd>
                    </div>
                    <div>
                      <dt>Ваше</dt>
                      <dd>{shownValue(conflict.field, conflict.mine) || '—'}</dd>
                    </div>
                  </dl>
                  <div className="platform-product__row">
                    <Button
                      onClick={() => setState((current) => serverTaken(current, conflict.field))}
                      actionId="platform.common-product.conflict-take-server"
                    >
                      Взять текущее
                    </Button>
                    <Button
                      onClick={() => setState((current) => mineKept(current, conflict.field))}
                      actionId="platform.common-product.conflict-keep-mine"
                    >
                      Оставить моё
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {loaded && (
          <>
            {!create && photos}

            {create && vm.prefill !== undefined && <ScanFrameBlock prefill={vm.prefill} />}

            <form
              className="platform-product__form"
              onSubmit={(event) => {
                event.preventDefault()
                save()
              }}
            >
              <ProductField
                label="Наименование"
                value={state.mine.name}
                onChange={(value) => setState((current) => fieldChanged(current, 'name', value))}
                disabled={!editable || locked}
                required
                note={noteOf(state, 'name')}
              />
              <ProductField
                label="Отображаемое имя"
                value={state.mine.displayName}
                onChange={(value) => setState((current) => fieldChanged(current, 'displayName', value))}
                disabled={!editable || locked}
                note={noteOf(state, 'displayName')}
                hint="Если задано, показывается вместо наименования. Пусто — показывается наименование."
              />
              <ProductField
                label="Артикул"
                value={state.mine.sku}
                onChange={(value) => setState((current) => fieldChanged(current, 'sku', value))}
                disabled={!editable || locked}
                note={noteOf(state, 'sku')}
              />
            </form>

            <section className="platform-product__section" aria-labelledby="platform-product-cash">
              <h3 id="platform-product-cash">
                <Coins size={16} aria-hidden="true" /> Кассовые поля
              </h3>
              {missingCash.length === 0 ? (
                <p className="platform-product__note platform-product__cash-summary" role="status">
                  <CheckCircle2 size={16} aria-hidden="true" /> Все кассовые поля определены: товар можно продавать.
                </p>
              ) : (
                <p className="platform-product__note platform-product__cash-summary" role="status">
                  <CircleHelp size={16} aria-hidden="true" /> Не определено: {missingCash.map((field) => CASH_FIELD_TITLES[field].toLowerCase()).join(', ')}.
                  Товар заводится, но своя касса его не продаёт, пока это не определено.
                </p>
              )}
              <div className="platform-product__form">
                <CashInput
                  label={CASH_FIELD_TITLES.vatRate}
                  field="vatRate"
                  value={state.mine.vatRate}
                  onChange={(value) => setState((current) => fieldChanged(current, 'vatRate', value))}
                  disabled={!editable || locked}
                  note={noteOf(state, 'vatRate')}
                  hint="Код ставки из словаря чека. Источники называют ставку сами: приоритет AQSI, затем 1С."
                />
                <ProductField
                  label={CASH_FIELD_TITLES.unitCode}
                  value={state.mine.unitCode}
                  onChange={(value) => setState((current) => fieldChanged(current, 'unitCode', value))}
                  disabled={!editable || locked}
                  inputMode="numeric"
                  note={noteOf(state, 'unitCode')}
                  hint="Цифры по ОКЕИ: 796 — штука, 166 — килограмм. Пусто — не знаю."
                  status={statusOf(state.mine.unitCode)}
                />
                <ProductField
                  label={CASH_FIELD_TITLES.unitName}
                  value={state.mine.unitName}
                  onChange={(value) => setState((current) => fieldChanged(current, 'unitName', value))}
                  disabled={!editable || locked}
                  note={noteOf(state, 'unitName')}
                  hint="Как единица печатается в чеке: «шт», «кг». До 32 символов. Пусто — не знаю."
                  status={statusOf(state.mine.unitName)}
                />
                <CashInput
                  label={CASH_FIELD_TITLES.isWeighted}
                  field="isWeighted"
                  value={state.mine.isWeighted}
                  onChange={(value) => setState((current) => fieldChanged(current, 'isWeighted', value))}
                  disabled={!editable || locked}
                  note={noteOf(state, 'isWeighted')}
                  hint="Весовой товар продаётся дробным количеством."
                />
                <CashInput
                  label={CASH_FIELD_TITLES.paymentSubject}
                  field="paymentSubject"
                  value={state.mine.paymentSubject}
                  onChange={(value) => setState((current) => fieldChanged(current, 'paymentSubject', value))}
                  disabled={!editable || locked}
                  note={noteOf(state, 'paymentSubject')}
                  hint="Тег 1212 чека. Пусто — не знаю: «Товар» само не подставляется."
                />
                <CashInput
                  label={CASH_FIELD_TITLES.paymentMethod}
                  field="paymentMethod"
                  value={state.mine.paymentMethod}
                  onChange={(value) => setState((current) => fieldChanged(current, 'paymentMethod', value))}
                  disabled={!editable || locked}
                  note={noteOf(state, 'paymentMethod')}
                  hint="Тег 1214 чека. Пусто — не знаю: «Полный расчёт» само не подставляется."
                />
              </div>
            </section>

            <section className="platform-product__section" aria-labelledby="platform-product-barcodes">
              <h3 id="platform-product-barcodes">
                <Barcode size={16} aria-hidden="true" /> Штрихкоды
              </h3>
              {state.mine.barcodes.length === 0 ? (
                <p className="platform-product__note">Штрихкодов нет.</p>
              ) : (
                <ul className="platform-product__list" aria-label="Штрихкоды товара">
                  {state.mine.barcodes.map((code) => (
                    <li key={code}>
                      <span className="platform-product__code">{code}</span>
                      <Button
                        variant="ghost"
                        icon={<Trash2 size={16} aria-hidden="true" />}
                        disabled={!editable || locked}
                        aria-label={`Удалить штрихкод ${code}`}
                        onClick={() => setState((current) => barcodeRemoved(current, code))}
                        actionId="platform.common-product.barcode-remove"
                      >
                        Удалить
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              {barcodesNote !== null && <p className="platform-product__note">{barcodesNote}</p>}
              <div className="platform-product__row platform-product__row--field">
                <ProductField
                  label="Новый штрихкод"
                  value={newBarcode}
                  onChange={setNewBarcode}
                  onEnter={addBarcode}
                  disabled={!editable || locked}
                  inputMode="numeric"
                />
                <Button
                  icon={<Plus size={16} aria-hidden="true" />}
                  disabled={!editable || locked || newBarcode.trim() === ''}
                  onClick={addBarcode}
                  actionId="platform.common-product.barcode-add"
                >
                  Добавить
                </Button>
              </div>
            </section>

            {!create && (
              <section className="platform-product__section" aria-labelledby="platform-product-sources">
                <h3 id="platform-product-sources">
                  <Link2 size={16} aria-hidden="true" /> Источники
                </h3>
                <p className="platform-product__note">
                  Что есть в общем каталоге и в источниках, сравнивает раздел «Синхронизация»: оттуда же значения переносятся в источники — вручную, по вашему решению.
                </p>
                {!vm.sources.loaded && <p className="platform-product__note">Связи с источниками ещё не загружены.</p>}
                {vm.sources.loaded && vm.sources.rows.length === 0 && vm.sources.hiddenCount === 0 && (
                  <p className="platform-product__note">Товар пока не связан ни с одним источником.</p>
                )}
                {vm.sources.rows.length > 0 && (
                  <ul className="platform-product__sources" aria-label="Связи товара с источниками">
                    {vm.sources.rows.map((source) => (
                      <li key={source.resourceId} className="platform-product__source">
                        <p className="platform-product__source-name">
                          <Link2 size={14} aria-hidden="true" /> {source.name}
                          <span className="platform-product__muted"> · {source.kindTitle}</span>
                        </p>
                        <p className="platform-product__muted">
                          Ключ карточки: {source.remoteKey} · {source.originText}
                        </p>
                        {renderSourceExtension?.(source)}
                      </li>
                    ))}
                  </ul>
                )}
                {vm.sources.hiddenCount > 0 && (
                  <p className="platform-product__note">Ещё связей: {vm.sources.hiddenCount} — нет доступа к этим источникам.</p>
                )}
                <div className="platform-product__row">
                  <Button onClick={actions.openSync} actionId="platform.common-product.open-sync">
                    Синхронизация
                  </Button>
                </div>
              </section>
            )}

            {!create && !archived && (
              <section className="platform-product__section" aria-label="Архив">
                <Button
                  variant="ghost"
                  icon={<Archive size={16} aria-hidden="true" />}
                  disabled={locked || dirty}
                  disabledReason={dirty ? 'Сначала сохраните или отмените правку.' : null}
                  onClick={() => setConfirmArchive(true)}
                  actionId="platform.common-product.archive"
                >
                  В архив
                </Button>
                <ActionError error={vm.archive.error} onReload={actions.reload} />
              </section>
            )}
          </>
        )}

        {duplicate !== null && duplicate.kind !== 'checking' && (
          <section className="platform-product__section platform-product__dup" aria-labelledby="platform-product-dup" role="alert">
            <h3 id="platform-product-dup">
              <Search size={16} aria-hidden="true" /> {duplicate.kind === 'found' ? 'Похоже, такой товар уже есть' : 'Не удалось проверить дубль'}
            </h3>
            {duplicate.kind === 'failed' && (
              <>
                <p className="platform-product__note">{duplicate.message}</p>
                <p className="platform-product__note">Создать без проверки? Если такой товар уже есть, получится второй.</p>
              </>
            )}
            {duplicate.kind === 'found' && (
              <ul className="platform-product__sources" aria-label="Похожие товары">
                {duplicate.matches.map((match) => (
                  <li key={match.id} className="platform-product__source">
                    <p className="platform-product__source-name">{match.name}</p>
                    <p className="platform-product__muted">
                      Артикул {match.sku ?? '—'} · {match.because}
                    </p>
                    <div className="platform-product__row">
                      {actions.duplicateAttach !== undefined && (
                        <Button
                          variant="primary"
                          icon={<ImagePlus size={16} aria-hidden="true" />}
                          onClick={() => actions.duplicateAttach?.(match.id)}
                          actionId="platform.common-product.duplicate-attach"
                        >
                          Это он — прикрепить фото
                        </Button>
                      )}
                      {actions.duplicateOpen !== undefined && (
                        <Button onClick={() => actions.duplicateOpen?.(match.id)} actionId="platform.common-product.duplicate-open">
                          Открыть
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="platform-product__row">
              <Button onClick={() => actions.duplicateDismiss?.()} actionId="platform.common-product.duplicate-back">
                Назад к форме
              </Button>
              <Button variant="danger" onClick={() => actions.duplicateCreateAnyway?.()} actionId="platform.common-product.duplicate-create-anyway">
                {duplicate.kind === 'found' ? 'Всё равно создать' : 'Создать без проверки'}
              </Button>
            </div>
          </section>
        )}

        {duplicate?.kind === 'checking' && (
          <p className="platform-product__note" role="status">
            Проверяю, нет ли такого товара в общем каталоге…
          </p>
        )}

        {photoRun !== null && (
          <section className="platform-product__section" aria-label="Фото нового товара">
            <p className="platform-product__note">
              {photoRun.phase === 'failed'
                ? 'Товар создан, фото не загружено — повторите.'
                : photoRun.phase === 'review'
                  ? 'Товар создан. Проверьте обрезку фото: «Принять» загрузит его, «Отмена» оставит товар без фото.'
                  : 'Товар создан, загружаю фото.'}
            </p>
            <ScanPhotoRun
              view={photoRun}
              onRetry={() => actions.photoRetry?.()}
              onCancel={() => actions.photoCancel?.()}
              onClose={() => actions.photoClose?.()}
              onAcceptCrop={(box) => actions.photoAcceptCrop?.(box)}
              extra={
                photoRun.phase === 'failed' && actions.openWithoutPhoto !== undefined ? (
                  <Button onClick={() => actions.openWithoutPhoto?.()} actionId="platform.common-product.open-without-photo">
                    Открыть товар без фото
                  </Button>
                ) : null
              }
            />
          </section>
        )}

        <footer className="platform-product__bar" aria-label="Действия с товаром">
          <ActionError error={vm.save.error} onReload={actions.reload} />
          {!dirty && vm.saved !== null && !vm.save.busy && vm.save.error === null && (
            <p className="platform-product__note" role="status">
              {vm.saved.replayed ? 'Эта правка уже была сохранена.' : `Сохранено в общем каталоге (версия ${vm.saved.version}).`}
              {product !== null && timeOf(product.updatedAt) !== '' ? ` Обновлён ${timeOf(product.updatedAt)}.` : ''}
            </p>
          )}
          {vm.save.busy && (
            <p className="platform-product__note" role="status">
              Сохраняю в общий каталог… Дождитесь ответа: повторное нажатие не нужно.
            </p>
          )}
          {dirty && blocker !== null && <p className="platform-product__note">{blocker}</p>}
          <div className="platform-product__buttons">
            {dirty && !archived ? (
              <>
                <Button
                  variant="primary"
                  icon={<Save size={16} aria-hidden="true" />}
                  busy={vm.save.busy}
                  disabled={!canSave}
                  disabledReason={conflicting ? 'Сначала решите, какие значения оставить.' : blocker}
                  onClick={save}
                  actionId="platform.common-product.save"
                >
                  {create ? 'Создать товар' : 'Сохранить'}
                </Button>
                <Button
                  icon={<Undo2 size={16} aria-hidden="true" />}
                  disabled={locked}
                  onClick={() => setConfirmDiscard(true)}
                  actionId="platform.common-product.discard"
                >
                  Отменить изменения
                </Button>
              </>
            ) : (
              <Button onClick={actions.close} actionId="platform.common-product.close">
                Закрыть
              </Button>
            )}
          </div>
        </footer>

        {confirmDiscard && (
          <ModalFrame title="Отменить изменения?" size="sm" covered={false} onClose={() => setConfirmDiscard(false)}>
            <div className="platform-product__confirm" role="alertdialog" aria-label="Отмена изменений">
              <p>Отменить несохранённые изменения? Поля вернутся к тому, что сохранено в общем каталоге.</p>
              <div className="platform-product__row">
                <Button onClick={() => setConfirmDiscard(false)} actionId="platform.common-product.discard-keep">
                  Нет
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setConfirmDiscard(false)
                    setState((current) => discarded(current))
                  }}
                  actionId="platform.common-product.discard-confirm"
                >
                  Да, отменить
                </Button>
              </div>
            </div>
          </ModalFrame>
        )}

        {confirmClose && (
          <ModalFrame title="Закрыть окно?" size="sm" covered={false} onClose={() => setConfirmClose(false)}>
            <div className="platform-product__confirm" role="alertdialog" aria-label="Несохранённые изменения">
              <p>Изменения не сохранены. Если закрыть окно, они пропадут.</p>
              <div className="platform-product__row">
                <Button onClick={() => setConfirmClose(false)} actionId="platform.common-product.guard-stay">
                  Остаться
                </Button>
                <Button variant="primary" onClick={closeNow} actionId="platform.common-product.guard-close">
                  Закрыть без сохранения
                </Button>
              </div>
            </div>
          </ModalFrame>
        )}

        {confirmArchive && product !== null && (
          <ModalFrame title="Отправить в архив?" size="sm" covered={false} onClose={() => setConfirmArchive(false)}>
            <div className="platform-product__confirm" role="alertdialog" aria-label="Подтверждение архива">
              <p>
                Отправить «{product.name}» в архив? Товар не удаляется физически, но правка закроется. Что уйдёт в источники,
                решают настройки удаления: по умолчанию ничего.
              </p>
              <div className="platform-product__row">
                <Button onClick={() => setConfirmArchive(false)} actionId="platform.common-product.archive-cancel">
                  Отмена
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setConfirmArchive(false)
                    actions.archive(state.version)
                  }}
                  actionId="platform.common-product.archive-confirm"
                >
                  В архив
                </Button>
              </div>
            </div>
          </ModalFrame>
        )}
      </section>
    </ActionInfoQuiet>
  )
}

/** Начальное состояние формы: создание из сканирования приходит с названием и штрихкодом — это уже «несохранённая правка». */
function startState(vm: CommonProductVm): EditorState {
  const base = initialState(vm.product)
  if (vm.mode !== 'create' || vm.prefill === undefined) return base
  const barcodes = vm.prefill.barcode === null ? [] : [vm.prefill.barcode]
  return { ...base, mine: { ...base.mine, name: vm.prefill.name, barcodes } }
}

/** Кадр сканирования: превью и подпись «Это фото станет главным»; кадр потерян — об этом сказано словами. */
function ScanFrameBlock({ prefill }: { prefill: NonNullable<CommonProductVm['prefill']> }) {
  return (
    <section className="platform-product__section" aria-label="Фото из сканера">
      <div className="platform-product__frame">
        {prefill.frameUrl === null ? (
          <span className="platform-product__noframe" role="img" aria-label="Снимка нет">
            <ImageOff size={24} aria-hidden="true" />
          </span>
        ) : (
          <img src={prefill.frameUrl} alt="Снимок из сканера" />
        )}
        <p className="platform-product__note">
          {prefill.frameUrl !== null
            ? 'Это фото станет главным. Название и штрихкод подставлены из снимка — проверьте и поправьте.'
            : prefill.frameLost
              ? 'Снимок потерян: страницу обновили. Название и штрихкод подставлены, а фото нет — снимите товар ещё раз или добавьте фото позже.'
              : 'Фото не будет: добавьте его в карточке товара.'}
        </p>
      </div>
    </section>
  )
}

/** Пометка поля: изменено и не сохранено — со значением, которое было. */
function noteOf(state: EditorState, field: ProductFieldKey): string | null {
  if (state.conflicts.some((conflict) => conflict.field === field)) return 'Разошлось с общим каталогом — решите выше.'
  const before = field === 'barcodes' ? state.base.barcodes.join(', ') : state.base[field].trim()
  const now = field === 'barcodes' ? state.mine.barcodes.join(', ') : state.mine[field].trim()
  const shown = shownValue(field, before)
  return before === now ? null : `Изменено, не сохранено. Было: «${shown === '' ? '—' : shown}»`
}

function ProductField({
  label,
  value,
  onChange,
  onEnter,
  disabled = false,
  required = false,
  inputMode,
  note = null,
  hint = null,
  status = null,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  onEnter?: () => void
  disabled?: boolean
  required?: boolean
  inputMode?: 'numeric'
  note?: string | null
  hint?: string | null
  /** Состояние кассового поля словом и значком (не только цветом); нет — без метки. */
  status?: CashStatus | null
}) {
  const id = useId()
  const noteId = `${id}-note`
  const text = [note, hint].filter((part): part is string => part !== null).join(' ')
  return (
    <div className="krp-field platform-product__field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
        {required ? ' *' : ''}
        {status !== null && <StatusTag status={status} />}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        required={required}
        inputMode={inputMode}
        aria-describedby={text === '' ? undefined : noteId}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || onEnter === undefined) return
          event.preventDefault()
          onEnter()
        }}
      />
      {text !== '' && (
        <p id={noteId} className="platform-product__note">
          {text}
        </p>
      )}
    </div>
  )
}

type CashStatus = 'known' | 'unknown'

const statusOf = (value: string): CashStatus => (value.trim() === '' ? 'unknown' : 'known')

/** Метка состояния кассового поля: значок и слово, а не только цвет. */
function StatusTag({ status }: { status: CashStatus }) {
  return status === 'unknown' ? (
    <span className="platform-product__state platform-product__state--unknown">
      <CircleHelp size={14} aria-hidden="true" /> не определено
    </span>
  ) : (
    <span className="platform-product__state">
      <CheckCircle2 size={14} aria-hidden="true" /> определено
    </span>
  )
}

/** Кассовое поле со списком выбора; первая строка — «Не знаю», она же очищает поле. */
function CashInput({
  label,
  field,
  value,
  onChange,
  disabled,
  note,
  hint,
}: {
  label: string
  field: 'vatRate' | 'isWeighted' | 'paymentSubject' | 'paymentMethod'
  value: string
  onChange: (value: string) => void
  disabled: boolean
  note: string | null
  hint: string
}) {
  const id = useId()
  const noteId = `${id}-note`
  const text = [note, hint].filter((part): part is string => part !== null).join(' ')
  return (
    <div className="krp-field platform-product__field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
        <StatusTag status={statusOf(value)} />
      </label>
      <ActionInfo id="platform.common-product.cash-field" label={label} layout="sibling">
      <select id={id} value={value} disabled={disabled} aria-describedby={noteId} onChange={(event) => onChange(event.target.value)}>
        <option value="">{UNKNOWN_LABEL}</option>
        {optionsFor(field, value).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      </ActionInfo>
      <p id={noteId} className="platform-product__note">
        {text}
      </p>
    </div>
  )
}
